import { Flex } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContractDto,
  SignedDocumentDto,
  TextDocumentDto,
  useCreateContractTextDocumentMutation,
  useListTextDocumentsForContractQuery,
} from "../../../../autogen/bff-api";
import { CombinedDocuments } from "../../../../common/documents/CombinedDocuments";
import { IncompleteFileUpload, isIncompleteFileUpload } from "../../../../common/documents/UppyUploader/FileRow";
import { useUppyUploader } from "../../../../common/documents/UppyUploader/useUppyUploader";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { loadContractThunk } from "../../../../common/redux/thunks/contract/load-contract-thunk";
import { useSub } from "../../../../common/subscription/useSub";
import { requireCounterpartyContext, requireDocumentFields } from "../../typeguards";

interface Props {
  contract: ContractDto;
  canEdit: boolean;
}

export const Documents = ({ contract, canEdit }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const sub = useSub();

  const documentFields = requireDocumentFields(contract);

  const [textDocuments, setTextDocuments] = useState<TextDocumentDto[]>([]);
  const [signedDocuments, setSignedDocuments] = useState<SignedDocumentDto[]>([]);

  const {
    data: textDocRes,
    isLoading: isLoadingTextDocuments,
    refetch: refetchTextDocuments,
  } = useListTextDocumentsForContractQuery({
    contractId: contract.id,
  });
  const [createTextDocument, { isLoading: isCreatingTextDocument }] = useCreateContractTextDocumentMutation();

  const createTextDocumentForContract = useCallback(() => {
    return createTextDocument({
      createContractTextDocumentRequest: {
        contractId: contract.id,
        name: "",
        text: "",
        tags: [],
      },
    }).unwrap();
  }, [contract.id, createTextDocument]);

  const counterpartyContext = requireCounterpartyContext(contract);

  const loadContract = useCallback(() => {
    dispatch(loadContractThunk({ contractId: contract.id }));
  }, [contract.id, dispatch]);

  const refetchDocuments = useCallback(() => {
    loadContract();
    void refetchTextDocuments();
  }, [loadContract, refetchTextDocuments]);

  const { uppy, allFiles, documents, setDocuments, setUploads, removeUpload } = useUppyUploader({
    entityId: contract.id,
    entityType: "Contract",
    reloadEntity: loadContract,
  });

  useEffect(() => {
    setDocuments(documentFields.documents);
  }, [documentFields.documents, setDocuments]);

  useEffect(() => {
    setSignedDocuments(documentFields.signedDocuments);
  }, [documentFields.signedDocuments]);

  useEffect(() => {
    setUploads(contract.editingMetaFields?.uploads ?? []);
  }, [contract.editingMetaFields?.uploads, setUploads]);

  useEffect(() => {
    setTextDocuments(textDocRes?.documents ?? []);
  }, [textDocRes?.documents]);

  return (
    <Flex width="full" flexDirection="column">
      {counterpartyContext && (
        <CombinedDocuments
          uploadedDocuments={documents}
          contractId={contract.id}
          signedDocuments={signedDocuments}
          setSignedDocuments={setSignedDocuments}
          refetchDocuments={refetchDocuments}
          textDocuments={textDocuments}
          isLoading={isLoadingTextDocuments}
          documentSignatureConfig={{
            contractId: contract.id,
            counterpartyContext,
          }}
          canSendForSigning={canEdit}
          isAiChatEnabled={sub.hasAiAccess}
          documentUploadConfig={
            canEdit
              ? {
                  dropTargetId: "viewContractDocumentsTarget",
                  uppy,
                  dropTargetHelperText: `${t("Upload your documents")}`,
                  uploads: allFiles.filter((f) => isIncompleteFileUpload(f)) as IncompleteFileUpload[],
                  removeUpload,
                }
              : undefined
          }
          sortConfig={{
            isCreatedAtSortEnabled: true,
            isCreatedBySortEnabled: true,
            isTitleSortEnabled: true,
          }}
          createTextDocument={createTextDocumentForContract}
          isCreatingTextDocument={isCreatingTextDocument}
          canEditTextDocuments={canEdit}
          isTagsColumnVisible={true}
          setUploadedDocuments={setDocuments}
          setTextDocuments={setTextDocuments}
          canEditTags={canEdit}
        />
      )}
    </Flex>
  );
};
